import React, { useState } from 'react';
import Layout from '../components/layout/Layout';
import Button from '../components/ui/Button';

const FAQ = () => {
  // State to track which FAQ items are expanded
  const [expandedItems, setExpandedItems] = useState({});
  
  // Toggle expanded state for an FAQ item
  const toggleItem = (id) => {
    setExpandedItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  
  // FAQ data organized by categories
  const faqData = [
    {
      category: "Products & Materials",
      items: [
        {
          id: "materials",
          question: "What materials do you use for your hats?",
          answer: "At Kala Hats, we use a variety of high-quality materials including premium straw, woven palm leaf, wool felt, and rabbit felt. Each material is carefully selected for its quality, durability, and suitability for the specific hat design. We prioritize natural materials that are both beautiful and long-lasting."
        },
        {
          id: "handmade",
          question: "Are all your hats truly handmade?",
          answer: "Yes, every Kala hat is 100% handcrafted by our skilled artisans in our Bali workshop. From shaping the crown to hand-stitching the sweatband and decorative elements, each step is performed by hand using traditional techniques. This hands-on approach allows us to maintain exceptional quality control and create truly unique pieces."
        },
        {
          id: "sizing",
          question: "How do I determine my hat size?",
          answer: "To find your hat size, measure the circumference of your head about 1/2 inch above your ears and eyebrows. Use a flexible measuring tape and measure in centimeters or inches. Compare your measurement to our size chart: XS (54-55cm/21.3-21.7\"), S (56-57cm/22-22.4\"), M (58-59cm/22.8-23.2\"), L (60-61cm/23.6-24\"), XL (62-63cm/24.4-24.8\"). If you're between sizes, we recommend sizing up. For custom orders, we'll work with you to ensure a perfect fit."
        },
        {
          id: "differences",
          question: "What's the difference between your hat styles?",
          answer: "Our collection features various hat styles, each with distinct characteristics. Fedoras have a creased crown and medium brim, perfect for a classic look. Panama hats are lightweight straw hats ideal for warm weather. Wide-brim sun hats offer maximum sun protection. Bucket hats have a casual, relaxed style with a downward-sloping brim. Each style is available in different materials and can be customized to your preferences."
        }
      ]
    },
    {
      category: "Custom Orders",
      items: [
        {
          id: "custom-process",
          question: "How does the custom hat ordering process work?",
          answer: "Our custom hat process begins when you submit a custom order request through our website or contact us directly. We'll discuss your design ideas, material preferences, and sizing requirements. Once we agree on the design and quote, we'll request a 50% deposit to begin production. We'll keep you updated throughout the crafting process and may send progress photos. When your hat is complete, we'll request the remaining payment before shipping your one-of-a-kind creation to you."
        },
        {
          id: "custom-time",
          question: "How long does it take to make a custom hat?",
          answer: "The production time for custom hats varies depending on the complexity of the design and our current workload. Simple designs typically take 1-2 weeks, while more complex hats may require 3-4 weeks. During peak seasons, production times may be longer. We'll provide you with a timeline estimate when you place your order and keep you updated on our progress."
        },
        {
          id: "custom-cost",
          question: "How much does a custom hat cost?",
          answer: "Custom hat prices vary depending on the complexity of the design, materials used, and time required. Basic custom hats typically start from around Rp 1,200,000, while more intricate designs can range from Rp 2,500,000 to Rp 5,000,000+. Factors that affect pricing include material quality, decorative elements, and design complexity. We'll provide a detailed quote after reviewing your custom order request."
        },
        {
          id: "custom-changes",
          question: "Can I request changes to my custom design?",
          answer: "Yes! After submitting your request, we'll create a design concept for your approval. You can request up to two rounds of revisions before production begins. Once production starts, major changes may incur additional costs. We encourage clear communication about your vision from the beginning to ensure we create exactly what you're looking for."
        }
      ]
    },
    {
      category: "Ordering & Shipping",
      items: [
        {
          id: "international",
          question: "Do you ship internationally?",
          answer: "Yes, we ship our handcrafted hats worldwide. International shipping costs and delivery times vary by location. Standard shipping typically takes 10-14 business days for most international destinations, while express shipping options (5-7 business days) are available at an additional cost. All customs fees, duties, and taxes are the responsibility of the recipient and are not included in our shipping charges."
        },
        {
          id: "shipping-time",
          question: "How long will it take to receive my order?",
          answer: "Shipping times vary depending on your location and the shipping method selected. For ready-made hats, we typically ship within 1-2 business days after receiving your order. For custom hats, shipping begins after the production is complete. Delivery times range from 2-3 business days for domestic Indonesian orders to 10-14 business days for international orders with standard shipping. Express shipping options are available for faster delivery."
        },
        {
          id: "tracking",
          question: "How can I track my order?",
          answer: "Once your order ships, you'll receive a confirmation email with tracking information. You can use this tracking number to monitor your package's journey. If you have any questions about your shipment or experience any issues with tracking, please contact us at info@kalahats.com or via WhatsApp at +62 812 8909 8912."
        },
        {
          id: "payment",
          question: "What payment methods do you accept?",
          answer: "We process payments via Xendit. Depending on activation status, customers can pay using cards (Visa, Mastercard, JCB, Amex), Virtual Account transfer, QRIS, e-wallet, and over-the-counter channels. Product catalog and checkout totals are displayed in IDR. For custom orders, we may request a deposit before production starts and settle the remaining balance before shipping."
        },
        {
          id: "recurring-payments",
          question: "Why does Kala Hats use recurring payments?",
          answer: "Recurring payments are used for our ongoing services, especially Kala Care Club membership (monthly hat maintenance, reshaping, and priority repair queue) and installment collection plans for approved custom projects. Customers explicitly authorize recurring charges, can view their billing schedule in advance, and can cancel future billing according to the service terms."
        }
      ]
    },
    {
      category: "Returns & Care",
      items: [
        {
          id: "returns",
          question: "What is your return policy?",
          answer: "We accept returns of unworn, unaltered ready-made hats within 14 days of delivery. Custom hats are not eligible for returns unless there is a manufacturing defect. To initiate a return, please contact us at returns@kalahats.com with your order number and reason for return. Please note that shipping costs are non-refundable, and a 15% restocking fee may apply. For full details, please visit our Shipping & Returns page."
        },
        {
          id: "exchanges",
          question: "Can I exchange my hat for a different size or style?",
          answer: "Yes, we're happy to facilitate exchanges for ready-made hats. Please follow the same process as returns, but indicate that you'd like an exchange instead of a refund. Exchanges are subject to product availability, and you may be responsible for additional shipping costs. Custom hats cannot be exchanged unless there is a manufacturing defect."
        },
        {
          id: "care",
          question: "How should I care for my hat?",
          answer: "For straw and woven hats: Store in a cool, dry place away from direct sunlight. Use a soft brush to remove dust. Avoid getting your hat wet; if it does get wet, let it dry naturally. Handle by the brim to avoid misshaping the crown. For felt and fabric hats: Use a hat brush to remove dust, brushing in a counterclockwise direction. For water spots, allow to dry completely then gently steam and brush. Store in a hatbox or on a hat stand. Each hat comes with specific care instructions tailored to its materials."
        },
        {
          id: "repairs",
          question: "Do you offer hat repair services?",
          answer: "Yes, we offer repair services for Kala hats. If your hat needs reshaping, cleaning, or repair of damaged elements, please contact us to discuss options. Repair costs vary depending on the extent of the work needed. For hats purchased from us, we offer discounted repair services. Please note that some extensive damage may not be repairable."
        }
      ]
    },
    {
      category: "Visiting & Contact",
      items: [
        {
          id: "visit",
          question: "Can I visit your workshop in Bali?",
          answer: "Yes! We welcome visitors to our workshop in Canggu, Bali. You can see our complete collection, watch our artisans at work (on select days), and discuss custom designs in person. Our showroom is open Monday through Saturday, 9:00 AM to 5:00 PM. We recommend scheduling an appointment to ensure we can provide you with the best experience. Please contact us via WhatsApp at +62 812 8909 8912 to arrange your visit."
        },
        {
          id: "wholesale",
          question: "Do you offer wholesale options for retailers?",
          answer: "Yes, we offer wholesale options for select retailers who align with our brand values. We can create a collection tailored to your store's aesthetic and customer base. For wholesale inquiries, please contact us at wholesale@kalahats.com with information about your business, and we'll be happy to discuss possibilities."
        },
        {
          id: "custom-bulk",
          question: "Can you create custom hats for events or corporate gifts?",
          answer: "Absolutely! We can create custom hats for weddings, corporate events, or as unique gifts. Whether you need a small batch of identical hats or individually customized pieces, we can accommodate your needs. For bulk orders, we offer tiered pricing based on quantity. Please contact us at least 6-8 weeks before your event to ensure timely delivery."
        },
        {
          id: "contact",
          question: "What's the best way to contact you with questions?",
          answer: "For general inquiries, you can reach us at info@kalahats.com or through our contact form on the website. For quicker responses, especially for custom order discussions, we recommend contacting us via WhatsApp at +62 812 8909 8912. We typically respond to all inquiries within 24-48 hours during business days."
        }
      ]
    }
  ];
  
  return (
    <Layout>
      {/* Hero Section */}
      <div className="bg-black py-12">
        <div className="container mx-auto px-4">
          <h1 className="font-serif text-4xl md:text-5xl font-bold text-primary mb-6 text-center">
            Frequently Asked Questions
          </h1>
          <p className="text-light max-w-3xl mx-auto text-center text-lg">
            Find answers to common questions about our handcrafted hats, custom orders, shipping, and more.
          </p>
        </div>
      </div>
      
      {/* FAQ Categories Navigation */}
      <section className="bg-secondary py-8 sticky top-0 z-10 border-b border-gray-800">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-4">
            {faqData.map(category => (
              <a 
                key={category.category} 
                href={`#${category.category.toLowerCase().replace(/\s+/g, '-')}`}
                className="px-4 py-2 bg-black/30 text-light hover:bg-primary hover:text-dark transition-colors duration-300 rounded-md"
              >
                {category.category}
              </a>
            ))}
          </div>
        </div>
      </section>
      
      {/* FAQ Content */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto space-y-16">
            {faqData.map(category => (
              <div 
                key={category.category} 
                id={category.category.toLowerCase().replace(/\s+/g, '-')}
                className="scroll-mt-24"
              >
                <h2 className="font-serif text-3xl font-bold text-primary mb-8">
                  {category.category}
                </h2>
                
                <div className="space-y-4">
                  {category.items.map(item => (
                    <div 
                      key={item.id} 
                      className="bg-secondary border border-gray-700 rounded-lg overflow-hidden"
                    >
                      <button
                        className="w-full px-6 py-4 text-left flex justify-between items-center focus:outline-none"
                        onClick={() => toggleItem(item.id)}
                      >
                        <h3 className="font-serif text-xl font-bold text-light">
                          {item.question}
                        </h3>
                        <span className="text-primary text-2xl">
                          {expandedItems[item.id] ? '−' : '+'}
                        </span>
                      </button>
                      
                      {expandedItems[item.id] && (
                        <div className="px-6 py-4 border-t border-gray-700">
                          <p className="text-gray-300">
                            {item.answer}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Still Have Questions */}
      <section className="py-16 bg-secondary">
        <div className="container mx-auto px-4 text-center">
          <h2 className="font-serif text-3xl font-bold text-primary mb-6">
            Still Have Questions?
          </h2>
          <p className="text-light max-w-2xl mx-auto mb-8">
            If you couldn't find the answer to your question, please don't hesitate to reach out. Our team is here to help.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Button to="/contact" variant="primary">
              Contact Us
            </Button>
            <Button 
              as="a" 
              href="https://wa.me/6281289098912" 
              target="_blank" 
              rel="noopener noreferrer"
              variant="outline"
            >
              WhatsApp Us
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default FAQ;
